/**
 * @voltxampere/presets — Bridge rectifier (#8)
 *
 * Four-diode bridge with DC input. With V1 = 10 V across the
 * bridge input pair, two diodes are forward-biased in series and
 * conduct, two are reverse-biased. V(out) settles at V1 minus
 * twice the Shockley drop ≈ 8.6 V. The load resistor sets the
 * conducting current.
 *
 * Net layout:
 *   in_pos / in_neg  — bridge input pair (driven by V1)
 *   out / gnd        — bridge output pair (load + ground)
 *
 * Diode connectivity (positive output corner is `out`, negative
 * corner is `gnd`):
 *   D1: in_pos → out      (forward when V(in_pos) > V(out))
 *   D2: gnd     → in_pos  (forward when V(gnd) > V(in_pos))
 *   D3: in_neg → out      (forward when V(in_neg) > V(out))
 *   D4: gnd     → in_neg  (forward when V(gnd) > V(in_neg))
 *
 * Sprint 25 R2 lane 4D-A — hand-authored layout. V1 vertical at
 * far-left supplies in_pos (top) and in_neg (bottom). The four
 * diodes are arranged as a 2×2 square with all four rotated to
 * 270° so cathodes point UP — the natural orientation for the
 * "out" rail at the top and the "gnd" rail at the bottom of the
 * bridge. RL vertical at far-right closes the load loop. Body
 * centers rounded to 20-px grid.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const bridgeRectifier = {
    id: "bridge-rectifier",
    name: { tr: "Köprü Doğrultucu", en: "Bridge Rectifier" },
    category: "filter",
    difficulty: 3,
    description: {
        tr: "Dört diyot köprü doğrultucu. V1=10V DC, RL=1kΩ → V(out) ≈ 10-2·Vd ≈ 8.6V.",
        en: "Four-diode bridge rectifier. V1=10V DC, RL=1kΩ → V(out) ≈ 10-2·Vd ≈ 8.6V.",
    },
    circuit: makeCircuit({
        slot: 8,
        name: "Bridge Rectifier",
        description: "Four-diode bridge rectifier with resistive load",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in_pos" }),
            makeNet({ name: "in_neg" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 10 },
                terminals: { pos: "net_in_pos", neg: "net_in_neg" },
                label: "V1",
                position: { x: -280, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "RL",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_out", p2: "net_gnd" },
                label: "RL",
                position: { x: 220, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "D1",
                type: "diode",
                params: { saturationCurrent: 1e-14, ideality: 1 },
                terminals: { anode: "net_in_pos", cathode: "net_out" },
                label: "D1",
                // rot 270 → A at (-80, -60) (down toward in_pos rail at y=0),
                // K at (-80, -100) (up toward out rail).
                position: { x: -80, y: -80 },
                rotation: 270,
            }),
            makeComponent({
                id: "D2",
                type: "diode",
                params: { saturationCurrent: 1e-14, ideality: 1 },
                terminals: { anode: "net_gnd", cathode: "net_in_pos" },
                label: "D2",
                // rot 270 → A at (-80, 100) (down toward gnd rail), K at
                // (-80, 60) (up toward in_pos rail at y=0).
                position: { x: -80, y: 80 },
                rotation: 270,
            }),
            makeComponent({
                id: "D3",
                type: "diode",
                params: { saturationCurrent: 1e-14, ideality: 1 },
                terminals: { anode: "net_in_neg", cathode: "net_out" },
                label: "D3",
                // rot 270 → A at (80, -60) (down), K at (80, -100) (up).
                position: { x: 80, y: -80 },
                rotation: 270,
            }),
            makeComponent({
                id: "D4",
                type: "diode",
                params: { saturationCurrent: 1e-14, ideality: 1 },
                terminals: { anode: "net_gnd", cathode: "net_in_neg" },
                label: "D4",
                // rot 270 → A at (80, 100) (down toward gnd), K at (80, 60)
                // (up toward in_neg rail at y=0).
                position: { x: 80, y: 80 },
                rotation: 270,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -280, y: 200 },
                rotation: 0,
            }),
        ],
        wires: [
            // in_pos bus: V1.pos (-280, -10) up over the top to D1.A
            // (-80, -60). The bus then drops down between D1 and D2 at
            // x=-80 to D2.K (-80, 60).
            {
                id: "w_in_pos_top",
                netId: "net_in_pos",
                path: [
                    { x: -280, y: -10 },
                    { x: -280, y: -40 },
                    { x: -80, y: -40 },
                    { x: -80, y: -60 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_in_pos_mid",
                netId: "net_in_pos",
                path: [
                    { x: -80, y: -60 },
                    { x: -80, y: 60 },
                ],
                style: "orthogonal",
            },
            // in_neg bus: V1.neg (-280, 10) routes around the bottom of
            // the bridge to reach D4.K (80, 60) and D3.A (80, -60).
            {
                id: "w_in_neg_v1",
                netId: "net_in_neg",
                path: [
                    { x: -280, y: 10 },
                    { x: -280, y: 40 },
                    { x: 80, y: 40 },
                    { x: 80, y: 60 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_in_neg_mid",
                netId: "net_in_neg",
                path: [
                    { x: 80, y: 60 },
                    { x: 80, y: -60 },
                ],
                style: "orthogonal",
            },
            // out rail: D1.K (-80, -100) → D3.K (80, -100) → RL.p1 (220, -20)
            {
                id: "w_out_top",
                netId: "net_out",
                path: [
                    { x: -80, y: -100 },
                    { x: 80, y: -100 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_out_to_rl",
                netId: "net_out",
                path: [
                    { x: 80, y: -100 },
                    { x: 220, y: -100 },
                    { x: 220, y: -20 },
                ],
                style: "orthogonal",
            },
            // gnd rail: D2.A (-80, 100) → D4.A (80, 100) → RL.p2 (220, 20)
            // → bottom rail across to GND.p (-280, 191).
            {
                id: "w_gnd_diodes",
                netId: "net_gnd",
                path: [
                    { x: -80, y: 100 },
                    { x: 80, y: 100 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd_to_rl",
                netId: "net_gnd",
                path: [
                    { x: 80, y: 100 },
                    { x: 220, y: 100 },
                    { x: 220, y: 20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd_bottom",
                netId: "net_gnd",
                path: [
                    { x: 220, y: 100 },
                    { x: 220, y: 230 },
                    { x: -280, y: 230 },
                    { x: -280, y: 191 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        // V_out ≈ 10 - 2·0.7 = 8.6 V. Tolerance accommodates the
        // Shockley equation's actual junction drop at this load
        // current (~9 mA → Vd ≈ 0.71 V each).
        {
            node: "out",
            analysis: "dc",
            value: 8.6,
            tolerance: 0.3,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Köprü doğrultucu AC veya DC giriş için tek polariteli çıkış üretir.",
            en: "Bridge rectifier produces single-polarity output for AC or DC input.",
        },
        {
            tr: "Çıkışta iki diyot ileri yönlü iletir → V_drop ≈ 2·Vd.",
            en: "Two diodes conduct in series at the output → V_drop ≈ 2·Vd.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 4"],
};
//# sourceMappingURL=bridge-rectifier.js.map